/**
 * Provider selection.
 *
 * Resolution order:
 *   1. `mock`                 - explicit request, allowed only outside production
 *   2. user-provided key      - OpenAI-compatible, supplied per request
 *   3. platform-managed key   - optional, disabled by default
 *   4. none                   - deterministic-only report
 *
 * Returning `null` is a first-class outcome: the deterministic engine already
 * produced a useful report, so the product degrades rather than fails.
 */

import type { AiProvider } from '../types';
import { MockProvider } from './mock';
import { OpenAiCompatibleProvider, normaliseBaseUrl } from './openai-compatible';

export const DEFAULT_BASE_URL = 'https://api.openai.com/v1';

export interface ProviderSelection {
  provider: AiProvider | null;
  providerName: string;
  mode: 'user_key' | 'platform' | 'mock' | 'none';
  /** Human-readable explanation shown in the UI. Contains no secrets. */
  note: string;
}

export interface PlatformProviderConfig {
  enabled: boolean;
  apiKey: string | null;
  baseUrl: string;
  model: string;
}

export interface SelectProviderInput {
  /** Provider requested by the caller. */
  requested: 'auto' | 'mock' | 'openai-compatible';
  /** Bring-your-own-key material, kept in memory only. */
  userKey: { apiKey: string; baseUrl?: string; model?: string } | null;
  platform: PlatformProviderConfig;
  allowMock: boolean;
  forceMock: boolean;
  timeoutMs: number;
}

const SHORT_KEY_MAX = 500;

export function selectProvider(input: SelectProviderInput): ProviderSelection {
  if (input.forceMock || input.requested === 'mock') {
    if (!input.allowMock && !input.forceMock) {
      return {
        provider: null,
        providerName: 'none',
        mode: 'none',
        note: 'The mock provider is disabled on this deployment.',
      };
    }
    return {
      provider: new MockProvider(),
      providerName: 'mock',
      mode: 'mock',
      note: 'Using the built-in deterministic mock provider. No external API was called.',
    };
  }

  if (input.userKey) {
    const apiKey = input.userKey.apiKey.trim();
    const baseUrl = normaliseBaseUrl(input.userKey.baseUrl ?? DEFAULT_BASE_URL);
    const model = (input.userKey.model ?? 'gpt-4o-mini').trim();
    if (apiKey.length > 0 && apiKey.length <= SHORT_KEY_MAX && baseUrl && model.length > 0) {
      return {
        provider: new OpenAiCompatibleProvider({
          name: 'openai-compatible',
          baseUrl,
          apiKey,
          model,
          timeoutMs: input.timeoutMs,
        }),
        providerName: 'openai-compatible',
        mode: 'user_key',
        note: 'Using the API key supplied with this request. The key is used for this diagnosis only and is never stored.',
      };
    }
  }

  if (input.platform.enabled && input.platform.apiKey) {
    const baseUrl = normaliseBaseUrl(input.platform.baseUrl);
    if (baseUrl) {
      return {
        provider: new OpenAiCompatibleProvider({
          name: 'platform',
          baseUrl,
          apiKey: input.platform.apiKey,
          model: input.platform.model,
          timeoutMs: input.timeoutMs,
        }),
        providerName: 'platform',
        mode: 'platform',
        note: 'Using the platform-managed AI provider.',
      };
    }
  }

  return {
    provider: null,
    providerName: 'none',
    mode: 'none',
    note: 'No AI provider was configured for this request, so the report contains deterministic analysis only.',
  };
}

export { MockProvider } from './mock';
export { OpenAiCompatibleProvider, normaliseBaseUrl } from './openai-compatible';
